import React from 'react';

// ============================================================
// Six unique logo concepts for Scatura Solutions
// Each returns a self-contained SVG (48x48 or 64x64 viewBox)
// Wordmark colour adapts to the surrounding text colour.
// ============================================================

// 1. PULSE - Concentric arcs from a centre dot.
//    Meaning: system heartbeat / resilience signal / ping.
//    Nod to the original Wix radar-node style.
export const PulseMark = ({ size = 40, color = '#4F46E5', accent = '#06B6D4' }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="24" r="3.4" fill={color} />
    <path d="M14 24a10 10 0 0 1 20 0" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M8 24a16 16 0 0 1 32 0" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeDasharray="1 3.5" fill="none" opacity="0.75" />
    <circle cx="34" cy="14" r="2.2" fill={accent} />
  </svg>
);

// 2. LOOP - S formed by two interlocking arcs (infinity-like).
//    Meaning: continuous feedback loop / self-healing / never-ending flow.
export const LoopMark = ({ size = 40, color = '#4F46E5', accent = '#A855F7' }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
    <defs>
      <linearGradient id="loopGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={color} />
        <stop offset="100%" stopColor={accent} />
      </linearGradient>
    </defs>
    <path
      d="M34 12 H22 a8 8 0 0 0 0 16 h4 a8 8 0 0 1 0 16 H14"
      stroke="url(#loopGrad)"
      strokeWidth="4.5"
      strokeLinecap="round"
      fill="none"
    />
    <circle cx="34" cy="12" r="2.6" fill={color} />
    <circle cx="14" cy="44" r="2.6" fill={accent} />
  </svg>
);

// 3. CONSTELLATION - Hex core with satellite nodes (modernised Wix radar).
//    Meaning: distributed systems / networked resilience / observability graph.
export const ConstellationMark = ({ size = 40, color = '#4F46E5', accent = '#06B6D4' }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
    <line x1="24" y1="24" x2="24" y2="8" stroke={color} strokeWidth="1.3" opacity="0.55" />
    <line x1="24" y1="24" x2="38" y2="18" stroke={color} strokeWidth="1.3" opacity="0.55" />
    <line x1="24" y1="24" x2="38" y2="34" stroke={color} strokeWidth="1.3" opacity="0.55" />
    <line x1="24" y1="24" x2="10" y2="34" stroke={color} strokeWidth="1.3" opacity="0.55" />
    <line x1="24" y1="24" x2="10" y2="18" stroke={color} strokeWidth="1.3" opacity="0.55" />
    <polygon
      points="24,17 30,20.5 30,27.5 24,31 18,27.5 18,20.5"
      fill={color}
    />
    <circle cx="24" cy="8" r="2.4" fill={accent} />
    <circle cx="38" cy="18" r="2.4" fill={color} />
    <circle cx="38" cy="34" r="2.4" fill={color} />
    <circle cx="10" cy="34" r="2.4" fill={color} />
    <circle cx="10" cy="18" r="2.4" fill={accent} />
  </svg>
);

// 4. SIGNAL - Chaos-to-recovery waveform with a spark peak.
//    Meaning: incident dip + recovery curve / MTTR / self-healing arc.
export const SignalMark = ({ size = 40, color = '#4F46E5', accent = '#10B981' }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
    <path
      d="M6 32 L14 32 L18 22 L24 38 L28 12 L34 26 L42 26"
      stroke={color}
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <circle cx="28" cy="12" r="3.2" fill={accent} />
    <circle cx="28" cy="12" r="6" fill={accent} opacity="0.2" />
  </svg>
);

// 5. SHIELD - Chevron shield with pulsing core dot.
//    Meaning: resilience / protection / systems that endure.
export const ShieldMark = ({ size = 40, color = '#4F46E5', accent = '#06B6D4' }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
    <defs>
      <linearGradient id="shieldGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} />
        <stop offset="100%" stopColor={accent} />
      </linearGradient>
    </defs>
    <path
      d="M24 5 L40 12 V24 c0 10 -7 17 -16 20 -9 -3 -16 -10 -16 -20 V12 Z"
      fill="url(#shieldGrad)"
      opacity="0.15"
    />
    <path
      d="M24 5 L40 12 V24 c0 10 -7 17 -16 20 -9 -3 -16 -10 -16 -20 V12 Z"
      stroke="url(#shieldGrad)"
      strokeWidth="2.6"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M17 24 L22 29 L31 19"
      stroke={color}
      strokeWidth="2.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

// 6. BRAID - Two strands twisting into one (partnership / redundancy / DNA).
//    Meaning: reliability through redundancy, teams braided with the client.
export const BraidMark = ({ size = 40, color = '#4F46E5', accent = '#F43F5E' }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
    <path
      d="M12 8 C 34 16, 14 32, 36 40"
      stroke={color}
      strokeWidth="3.4"
      strokeLinecap="round"
      fill="none"
    />
    <path
      d="M36 8 C 14 16, 34 32, 12 40"
      stroke={accent}
      strokeWidth="3.4"
      strokeLinecap="round"
      fill="none"
    />
    <circle cx="24" cy="24" r="3" fill="white" stroke={color} strokeWidth="1.8" />
  </svg>
);

// ============================================================
// Preview page
// ============================================================

const CONCEPTS = [
  {
    id: 'pulse',
    name: 'Pulse',
    tagline: 'System heartbeat. Resilience signal.',
    story:
      'A centre core with two concentric arcs, one dashed. Nods to the original Wix radar-node logo but stripped down for a modern tech mark. Reads as ping, sonar, heartbeat, or a system coming online.',
    Mark: PulseMark,
  },
  {
    id: 'loop',
    name: 'Loop',
    tagline: 'Continuous feedback. Self-healing flow.',
    story:
      'A stylised S built from two interlocking arcs. Directly references the "S" in Scatura while symbolising closed-loop control, feedback and self-healing systems.',
    Mark: LoopMark,
  },
  {
    id: 'constellation',
    name: 'Constellation',
    tagline: 'Distributed systems. Networked resilience.',
    story:
      'Hexagonal core surrounded by satellite nodes, connected by faint lines. A direct evolution of the original Wix radar logo - modern, technical, and unmistakably "systems".',
    Mark: ConstellationMark,
  },
  {
    id: 'signal',
    name: 'Signal',
    tagline: 'Incident. Recovery. Peak performance.',
    story:
      'A waveform that dips and recovers with a bright spark at the peak. Tells the entire Scatura story in one glyph: chaos hits, systems recover, teams end up stronger.',
    Mark: SignalMark,
  },
  {
    id: 'shield',
    name: 'Shield',
    tagline: 'Resilience engineered. Protection delivered.',
    story:
      'A chevron shield with a checkmark core. Immediately reads as security, reliability and trust - useful when selling into regulated industries.',
    Mark: ShieldMark,
  },
  {
    id: 'braid',
    name: 'Braid',
    tagline: 'Two strands. One outcome.',
    story:
      'Two strands twisting past each other with a small joining node in the middle. Signals partnership, redundancy and the idea of Scatura teams braided with the client team.',
    Mark: BraidMark,
  },
];

const Card = ({ concept }) => {
  const { name, tagline, story, Mark } = concept;
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      {/* Light preview */}
      <div className="rounded-2xl bg-slate-50 border border-slate-200/70 p-6 flex items-center justify-center h-40">
        <Mark size={72} />
      </div>
      {/* Dark preview */}
      <div className="mt-3 rounded-2xl bg-slate-950 p-6 flex items-center justify-center h-32">
        <Mark size={56} color="#ffffff" accent="#22D3EE" />
      </div>

      {/* In-context: navbar-sized wordmark */}
      <div className="mt-3 rounded-2xl border border-slate-200/70 bg-white px-4 py-3 flex items-center gap-2.5">
        <Mark size={32} />
        <div className="leading-tight">
          <div className="text-[14px] font-semibold text-slate-900 tracking-tight">Scatura</div>
          <div className="text-[9.5px] text-slate-500 uppercase tracking-[0.18em]">Solutions</div>
        </div>
      </div>

      <h3 className="mt-5 font-display text-[22px] font-semibold text-slate-900 tracking-tight">
        {name}
      </h3>
      <p className="mt-0.5 text-[13.5px] text-indigo-600 font-medium">{tagline}</p>
      <p className="mt-3 text-[13.5px] leading-relaxed text-slate-600">{story}</p>
    </div>
  );
};

const LogoPreview = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/40 to-white py-14 lg:py-20">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[12px] font-medium text-slate-700 uppercase tracking-[0.18em]">
            Logo Concepts
          </div>
          <h1 className="mt-4 font-display text-[36px] md:text-[48px] leading-[1.05] font-semibold tracking-tight text-slate-900">
            Six directions for the{' '}
            <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 bg-clip-text text-transparent">
              Scatura mark.
            </span>
          </h1>
          <p className="mt-5 text-[16px] leading-relaxed text-slate-600">
            Each concept is shown on a light background, a dark background, and inside a navbar lockup with the wordmark
            - the three most common contexts. Reply with the concept name you want and I&rsquo;ll wire it into
            the live site (Navbar + Footer + Contact form + Hero).
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CONCEPTS.map((c) => (
            <Card key={c.id} concept={c} />
          ))}
        </div>

        <div className="mt-14 rounded-2xl border border-slate-200 bg-white p-6 text-[14px] text-slate-600 leading-relaxed">
          <div className="font-semibold text-slate-900">How to choose</div>
          <ul className="mt-2 space-y-1 list-disc pl-5">
            <li><b>Pulse</b> - closest to the original Wix radar-node style, refreshed and minimal.</li>
            <li><b>Loop</b> - keeps the "S" reference while telling a self-healing / feedback-loop story.</li>
            <li><b>Constellation</b> - boldest technical mark; ideal if you want to look like a systems / distributed-computing brand.</li>
            <li><b>Signal</b> - tells the entire brand story (chaos + recovery) in one glyph.</li>
            <li><b>Shield</b> - strongest "trust / reliability / security" read; best for regulated buyers.</li>
            <li><b>Braid</b> - most human / partnership-oriented; best if training &amp; coaching is a core sale.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LogoPreview;
